// CreateCertifictae1.jsx
// Redesigned certificate creation flow:
//   Phase 1 — Select: Branch → Class → Section → Student → Certificate Type + Template
//   Phase 2 — Fill Details (auto-filled from student) + Live Preview
//   Phase 3 — Generated table with Print / PDF / View actions

import React, { useState, useMemo, useRef, useEffect } from 'react';
import Select from 'react-select';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  extractVariablesFromHtml,
  SAMPLE_DATA,
  VARIABLE_LABEL_MAP,
} from './templates/templateEngine';
import { renderCertificateForStudent, inlineImagesForCapture } from './DynamicCertificateBuilder';
import TemplatePreview from './TemplatePreview';

// ─── Constants ────────────────────────────────────────────────────────────────

const STREAM_CLASS_IDS = [15, 16];
const isStreamClass = (classId) => STREAM_CLASS_IDS.includes(parseInt(classId));

const TYPE_COLORS = {
  TC:          '#1a237e',
  BONAFIDE:    '#B8860B',
  STUDY:       '#7c3aed',
  RECORDSHEET: '#374151',
  IDCARD:      '#c2185b',
};

const TYPE_LABELS = {
  tc:          'Transfer Certificate',
  bonafide:    'Bonafide Certificate',
  study:       'Study Certificate',
  recordsheet: 'Record Sheet',
  idcard:      'Student ID Card',
};

const FIELD_TYPES = {
  dob: 'date', leaving_date: 'date', date: 'date',
  working_days: 'number', present_days: 'number',
};

const FIELD_OPTIONS = {
  exam_result: ['PASS', 'FAIL'],
  conduct:     ['GOOD', 'VERY GOOD', 'EXCELLENT'],
};

const fieldLabel = (key) =>
  VARIABLE_LABEL_MAP[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// Map any raw cert-type string to the uppercase filter key used in TemplatePicker
const toFilterKey = (raw) => {
  const t = String(raw || '').toLowerCase().replace(/[\s_-]/g, '');
  if (t === 'idcard' || t.includes('idcard')) return 'IDCARD';
  if (t === 'bonafide') return 'BONAFIDE';
  if (t === 'study') return 'STUDY';
  if (t === 'recordsheet' || t === 'record') return 'RECORDSHEET';
  if (t === 'tc' || t === 'transfer' || t.includes('transfer')) return 'TC';
  return String(raw || '').toUpperCase();
};

// Normalise a profile-photo value into a usable <img src>.
//  - data: URIs and http(s)/protocol-relative URLs are used as-is
//  - a bare base64 image payload (no data: prefix) gets the right data:image prefix
//  - anything else (incl. empty/"null") returns '' so the silhouette fallback shows
const normalizePhotoSrc = (v) => {
  if (!v) return '';
  const s = String(v).trim();
  if (!s || s === 'null' || s === 'undefined') return '';
  if (s.startsWith('data:') || /^https?:\/\//i.test(s) || s.startsWith('//')) return s;
  // Detect raw base64 by image signature, then by a long base64-only body
  if (/^\/9j\//.test(s))            return `data:image/jpeg;base64,${s}`; // JPEG
  if (/^iVBORw0KGgo/.test(s))       return `data:image/png;base64,${s}`;  // PNG
  if (/^R0lGOD/.test(s))            return `data:image/gif;base64,${s}`;  // GIF
  if (/^[A-Za-z0-9+/]{200,}={0,2}$/.test(s)) return `data:image/jpeg;base64,${s}`;
  return s; // relative/other URL — let the browser try; onerror falls back
};

// ─── Shared label/input style ─────────────────────────────────────────────────

const ciStyle = {
  label: {
    display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5,
  },
  input: {
    width: '100%', border: '1.5px solid #d1d5db', borderRadius: 8,
    padding: '9px 12px', fontSize: 13, color: '#1f2937',
    background: '#fff', outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
};

// ─── Phase 1: Selection Panel ─────────────────────────────────────────────────

const SelectionPanel = ({
  branches, allClasses, streams = [], students, certificateTypes, savedTemplates,
  existingCertificates, academicYears = [],
  onComplete,
  onReset,
}) => {
  const [selAcademicYear, setSelAcademicYear] = useState('');
  const [selBranch,   setSelBranch]   = useState('');
  const [selClass,    setSelClass]    = useState('');
  const [selStream,   setSelStream]   = useState('');
  const [selSection,  setSelSection]  = useState('');
  const [selStudent,  setSelStudent]  = useState(null);
  const [selCertTypeId, setSelCertTypeId] = useState(''); // backend id
  const [errors, setErrors]           = useState({});    // per-field validation (stream/section)
  const [selTemplateId, setSelTemplateId] = useState(''); // chosen template id
  const [dupError, setDupError]       = useState(null);  // duplicate certificate warning
  const [tplWarning, setTplWarning]   = useState(null);  // no-template-available warning

  // Academic year is optional and selected manually — no default is applied.

  // ── Derived dropdown options ─────────────────────────────────────────────

  const branchObj = branches.find(b => String(b.id) === selBranch);

  // Sections to choose from: the selected branch's sections, or — when no branch
  // is picked ("All Branches") — every branch's sections pooled together, so the
  // Section dropdown is never empty just because a branch wasn't selected.
  const sourceSections = useMemo(
    () => (branchObj ? (branchObj.sections || []) : branches.flatMap(b => b.sections || [])),
    [branchObj, branches]
  );

  // Class options: scoped to the chosen branch if one is selected, else all classes.
  const classOpts = useMemo(() => {
    if (branchObj) {
      const ids = [...new Set(branchObj.sections.map(s => String(s.class_id)))];
      return allClasses
        .filter(c => ids.includes(String(c.id)))
        .sort((a, b) => Number(a.id) - Number(b.id));
    }
    return [...allClasses].sort((a, b) => Number(a.id) - Number(b.id));
  }, [branchObj, allClasses]);

  // Stream options — only for Intermediate classes (15/16); distinct stream_ids
  // among the selected branch's sections for selClass, labelled via the streams prop.
  const streamOpts = useMemo(() => {
    if (!isStreamClass(selClass)) return [];
    const ids = [...new Set(
      sourceSections
        .filter(s => String(s.class_id) === selClass && s.stream_id != null)
        .map(s => String(s.stream_id))
    )];
    return ids
      .map(id => {
        const st = streams.find(x => String(x.id) === id);
        return st ? { value: String(st.id), label: st.stream } : null;
      })
      .filter(Boolean);
  }, [sourceSections, selClass, streams]);

  const showStream = isStreamClass(selClass) && streamOpts.length > 0;

  const sectionOpts = useMemo(() => {
    const filtered = sourceSections
      // Narrow by class only when a class is chosen; with "All Classes" selected,
      // show every section so the Section dropdown works by default.
      .filter(s => !selClass || String(s.class_id) === selClass)
      // Only narrow by stream when a stream picker is actually shown AND chosen.
      // (The old `!isStreamClass` test wiped every section for Intermediate
      //  classes that have no stream metadata, leaving the dropdown empty.)
      .filter(s => !showStream || !selStream || String(s.stream_id) === String(selStream));
    // Pooling sections across branches (when no branch is selected) can repeat a
    // section name — dedupe by name so the list is clean; students are matched by name.
    const seen = new Set();
    const unique = [];
    filtered.forEach(s => {
      if (s.section_name && !seen.has(s.section_name)) { seen.add(s.section_name); unique.push(s); }
    });
    return unique.sort((a, b) => a.section_name.localeCompare(b.section_name));
  }, [sourceSections, selClass, selStream, showStream]);

  const studentOpts = useMemo(() =>
    students
      .filter(s =>
        (!selAcademicYear || String(s.academic_year_id) === selAcademicYear) &&
        (!selBranch       || String(s.branch_id)         === selBranch)  &&
        (!selClass        || String(s.class_id)          === selClass)   &&
        // Section is matched by NAME (the dropdown value), so it works whether or
        // not a branch is chosen — section ids differ across branches, names don't.
        (!selSection      || String(s.section_name)      === selSection)
      )
      .sort((a, b) => (a.student_name || '').localeCompare(b.student_name || '')),
    [students, selAcademicYear, selBranch, selClass, selSection]
  );

  // Certificate Type — raw backend labels, deduplicated by id
  const certTypeOpts = useMemo(() =>
    certificateTypes.map(ct => ({ id: String(ct.id), label: ct.certificate_type })),
    [certificateTypes]
  );

  // Resolve selected cert-type object
  const selCertTypeObj = certificateTypes.find(ct => String(ct.id) === selCertTypeId) || null;
  // filter key for matching templates
  const selCertFilterKey = selCertTypeObj ? toFilterKey(selCertTypeObj.certificate_type) : '';

  // Build all templates (saved backend + built-in TEMPLATE_LIBRARY)
  const typeMap = useMemo(() => Object.fromEntries(
    certificateTypes.map(ct => [String(ct.id), ct.certificate_type])
  ), [certificateTypes]);

  // Only backend-saved templates — no built-in presets
  const allTemplates = useMemo(() => {
    return savedTemplates.map(t => {
      let certTypeFromJson = '';
      try { certTypeFromJson = JSON.parse(t.template)?.certType || ''; } catch {}
      const rawType = certTypeFromJson || typeMap[String(t.certificate_type_id)] || 'TC';
      return {
        id:                  String(t.id),
        template_name:       t.template_name,
        template_type:       toFilterKey(rawType),
        certificate_type_id: t.certificate_type_id,
        branch_id:           String(t.branch_id || ''),   // ← carry branch from backend
        template_json:       t.template,
        _source:             'saved',
      };
    });
  }, [savedTemplates, typeMap]);

  // Templates matching the selected cert type, preferring the selected branch.
  // The template dropdown has been removed — a matching template is auto-resolved.
  const templateOpts = useMemo(() => {
    if (!selCertFilterKey) return [];
    return allTemplates.filter(t => t.template_type === selCertFilterKey);
  }, [allTemplates, selCertFilterKey]);

  // Auto-resolve a template for the chosen cert type: prefer one for the selected
  // branch, otherwise fall back to any template of that type.
  const selTemplate = useMemo(() => {
    if (!templateOpts.length) return null;
    if (selBranch) {
      const branchMatch = templateOpts.find(t => t.branch_id === selBranch);
      if (branchMatch) return branchMatch;
    }
    return templateOpts[0];
  }, [templateOpts, selBranch]);

  // ── Auto-proceed once a Student and Certificate Type are chosen ───────────
  useEffect(() => {
    // Reset DetailsPhase whenever any selection changes
    if (onReset) onReset();
    setDupError(null);
    setTplWarning(null);

    if (!(selStudent && selCertTypeId)) return;

    // Duplicate-certificate restriction removed — a student may be issued the same
    // certificate type more than once.

    // No saved template exists for this certificate type — cannot render the form.
    if (!selTemplate) {
      setTplWarning(
        `No template is available for the selected certificate type. ` +
        `Please create one in the Template Builder first.`
      );
      return;
    }

    const branchObjFinal = branches.find(b => String(b.id) === selBranch);
    const className      = selStudent.class_name
      || allClasses.find(c => String(c.id) === String(selStudent.class_id))?.class || '';
    const sectionName    = selStudent.section_name
      || (branchObjFinal?.sections || []).find(s => String(s.id) === String(selStudent.section_id))?.section_name || '';

    onComplete({
      student:       selStudent,
      template:      selTemplate,
      className,
      sectionName,
      certTypeKey:   selCertFilterKey,
      certTypeId:    selCertTypeId,
      // Pass branch empty when the user did not pick one (backend skips the filter).
      branchId:      selBranch || '',
      academicYearId: selAcademicYear,
    });
  }, [selStudent, selCertTypeId, selTemplate, selBranch, selClass, selSection, selAcademicYear]);

  // ── Reset every field back to its default (clears the form) ───────────────
  const handleResetFields = () => {
    setSelAcademicYear('');
    setSelBranch('');
    setSelClass('');
    setSelSection('');
    setSelStudent(null);
    setSelCertTypeId('');
    setDupError(null);
    setTplWarning(null);
    if (onReset) onReset();
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ width: '100%' }}>
      <div style={card}>

        {/* Header */}
        <div style={cardHdr}>
          <div style={iconWrap('#6366f115', '#6366f1')}>
            <i className="fal fa-user-graduate" />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>
              Create Certificate
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
              Pick a student and certificate type to continue. Year, branch, class and section are optional filters.
            </div>
          </div>

          {/* Refresh — clears all selections */}
          <button
            type="button"
            onClick={handleResetFields}
            title="Reset all fields"
            style={{
              marginLeft: 'auto', flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: 7,
              background: '#fff', color: '#6366f1',
              border: '1.5px solid #c7d2fe', borderRadius: 9,
              padding: '8px 14px', fontWeight: 700, fontSize: 13,
              cursor: 'pointer', transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#eef2ff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
          >
            <i className="fal fa-sync-alt" /> Refresh
          </button>
        </div>

        {/* ── Row 1: Academic Year · Branch · Class · Section ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>

          {/* Academic Year — optional filter, selected manually */}
          <div>
            <label style={ciStyle.label}>Academic Year</label>
            <select
              value={selAcademicYear}
              onChange={e => setSelAcademicYear(e.target.value)}
              style={{ ...ciStyle.input, borderColor: '#d1d5db' }}
            >
              <option value="">-- Select Year --</option>
              {academicYears.map(y => (
                <option key={y.id} value={String(y.id)}>{y.academic_year}</option>
              ))}
            </select>
          </div>

          {/* Branch — optional filter */}
          <div>
            <label style={ciStyle.label}>Branch</label>
            <select
              value={selBranch}
              onChange={e => {
                setSelBranch(e.target.value);
                setSelClass(''); setSelStream(''); setSelSection(''); setSelStudent(null);
                setSelCertTypeId('');
                setErrors({});
              }}
              style={{ ...ciStyle.input, borderColor: '#d1d5db' }}
            >
              <option value="">-- All Branches --</option>
              {branches.map(b => (
                <option key={b.id} value={String(b.id)}>{b.branch_name}</option>
              ))}
            </select>
          </div>

          {/* Class — optional filter */}
          <div>
            <label style={ciStyle.label}>Class</label>
            <select
              value={selClass}
              onChange={e => {
                setSelClass(e.target.value);
                setSelStream(''); setSelSection(''); setSelStudent(null);
                setSelCertTypeId('');
                setErrors({});
              }}
              style={{ ...ciStyle.input, borderColor: '#d1d5db' }}
            >
              <option value="">-- All Classes --</option>
              {classOpts.map(c => (
                <option key={c.id} value={String(c.id)}>{c.class}</option>
              ))}
            </select>
          </div>

          {/* Stream — only for Intermediate classes (15/16) */}
          {showStream && (
            <div>
              <label style={ciStyle.label}>Stream <Req /></label>
              <select
                value={selStream}
                onChange={e => {
                  setSelStream(e.target.value);
                  setSelSection(''); setSelStudent(null);
                  setSelCertTypeId('');
                  setErrors({});
                }}
                disabled={!selClass}
                style={{ ...ciStyle.input, borderColor: errors.stream ? '#ef4444' : '#d1d5db', opacity: !selClass ? 0.55 : 1 }}
              >
                <option value="">-- Select Stream --</option>
                {streamOpts.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {errors.stream && <ErrMsg>Required</ErrMsg>}
            </div>
          )}

          {/* Section */}
          <div>
            <label style={ciStyle.label}>Section <Req /></label>
            <select
              value={selSection}
              onChange={e => {
                setSelSection(e.target.value);
                setSelStudent(null);
                setSelCertTypeId('');
                setErrors({});
              }}
              disabled={showStream && !selStream}
              style={{ ...ciStyle.input, borderColor: errors.section ? '#ef4444' : '#d1d5db', opacity: (showStream && !selStream) ? 0.55 : 1 }}
            >
              <option value="">-- All Sections --</option>
              {sectionOpts.map(s => (
                <option key={s.section_name} value={s.section_name}>{s.section_name}</option>
              ))}
            </select>
          </div>

        </div>

        {/* ── Divider ── */}
        <div style={{ borderTop: '1.5px dashed #e2e8f0', margin: '4px 0 16px' }} />

        {/* ── Row 2: Student Name · Certificate Type ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>

          {/* Student — searchable; shows all students by default, narrowed by the filters above */}
          <div>
            <label style={ciStyle.label}>Student Name <Req /></label>
            <Select
              classNamePrefix="cert-student-select"
              placeholder="Search or select student…"
              isClearable
              noOptionsMessage={() => 'No students found'}
              options={studentOpts.map(s => ({
                value: String(s.student_id),
                label: s.admission_no ? `${s.student_name} - ${s.admission_no}` : s.student_name,
                student: s,
              }))}
              value={selStudent ? {
                value: String(selStudent.student_id),
                label: selStudent.admission_no
                  ? `${selStudent.student_name} - ${selStudent.admission_no}`
                  : selStudent.student_name,
              } : null}
              onChange={opt => setSelStudent(opt ? opt.student : null)}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={{
                control: (base, state) => ({
                  ...base,
                  minHeight: 38,
                  borderRadius: 8,
                  borderWidth: 1.5,
                  borderColor: state.isFocused ? '#6366f1' : '#d1d5db',
                  boxShadow: 'none',
                  fontSize: 13,
                  '&:hover': { borderColor: '#6366f1' },
                }),
                valueContainer: base => ({ ...base, padding: '2px 10px' }),
                placeholder: base => ({ ...base, color: '#9ca3af' }),
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                menu: base => ({ ...base, fontSize: 13 }),
                option: (base, state) => ({
                  ...base,
                  fontSize: 13,
                  backgroundColor: state.isSelected ? '#6366f1' : state.isFocused ? '#eef2ff' : '#fff',
                  color: state.isSelected ? '#fff' : '#1f2937',
                }),
              }}
            />
          </div>

          {/* Certificate Type — backend values */}
          <div>
            <label style={ciStyle.label}>Certificate Type <Req /></label>
            <select
              value={selCertTypeId}
              onChange={e => setSelCertTypeId(e.target.value)}
              style={{ ...ciStyle.input, borderColor: '#d1d5db' }}
            >
              <option value="">-- Select Certificate Type --</option>
              {certTypeOpts.map(o => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>

        </div>

        {/* Student info chip — shown once student is selected */}
        {selStudent && (
          <div style={{ marginTop: 16, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#15803d' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <i className="fal fa-check-circle" />
              <strong>{selStudent.student_name}</strong>
              {selStudent.roll_no && <span style={{ color: '#166534' }}> · Roll No: {selStudent.roll_no}</span>}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px', fontSize: 12, color: '#166534', paddingLeft: 22 }}>
              {(selStudent.father_name || selStudent.parent_name) && (
                <span>Father: <strong>{selStudent.father_name || selStudent.parent_name}</strong></span>
              )}
              {selStudent.mother_name && (
                <span>Mother: <strong>{selStudent.mother_name}</strong></span>
              )}
              {(selStudent.student_mobile || selStudent.parent_mobile) && (
                <span>Mobile: <strong>{selStudent.student_mobile || selStudent.parent_mobile}</strong></span>
              )}
              {selStudent.class_name && (
                <span>Class: <strong>{selStudent.class_name}</strong></span>
              )}
            </div>
          </div>
        )}

        {/* Duplicate certificate error banner */}
        {dupError && (
          <div style={{
            marginTop: 14,
            background: '#fef2f2',
            border: '1.5px solid #fca5a5',
            borderRadius: 10,
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: '#fee2e2', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className="fal fa-ban" style={{ color: '#dc2626', fontSize: 17 }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#dc2626', marginBottom: 3 }}>
                Certificate Already Issued
              </div>
              <div style={{ fontSize: 12, color: '#b91c1c', lineHeight: 1.55 }}>
                {dupError}
              </div>
            </div>
          </div>
        )}

        {/* No-template warning banner */}
        {tplWarning && (
          <div style={{
            marginTop: 14,
            background: '#fffbeb',
            border: '1.5px solid #fcd34d',
            borderRadius: 10,
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: '#fef3c7', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className="fal fa-exclamation-triangle" style={{ color: '#d97706', fontSize: 17 }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#b45309', marginBottom: 3 }}>
                No Template Available
              </div>
              <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.55 }}>
                {tplWarning}
              </div>
            </div>
          </div>
        )}

        {/* Progress hint */}
        {!dupError && !tplWarning && (selStudent || selCertTypeId) && (
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#9ca3af' }}>
            <i className="fal fa-info-circle" style={{ color: '#6366f1' }} />
            {!selStudent        ? 'Now select a Student'
              : !selCertTypeId  ? 'Now select a Certificate Type'
              : <span style={{ color: '#10b981', fontWeight: 600 }}>✓ All set — loading form…</span>
            }
          </div>
        )}

      </div>
    </div>
  );
};

// ─── Phase 2: Fill Details + Live Preview ─────────────────────────────────────

const isRawPhotoField = (key) => key === 'student_photo';
const isImageField    = (key) => !isRawPhotoField(key) && /photo|image|img|signature|sign|logo|stamp|seal/i.test(key);

// ── Form layout config per certificate type ───────────────────────────────────
// cols     : grid column count
// gap      : [rowGap, colGap]
// inputPad : padding for each input / select
// fontSize : input font size
// labelSize: label font size
// accent   : colour used for section headings / highlights

const CERT_FORM_CONFIG = {
  // TC — many fields → 3-column compact
  TC: {
    cols: 3, gap: '13px 16px',
    inputPad: '8px 11px', fontSize: 12.5, labelSize: 10,
    accent: '#1a237e', accentBg: '#e8eaf6',
    description: 'Transfer Certificate — compact 3-column layout',
  },
  // Record Sheet — similarly dense
  RECORDSHEET: {
    cols: 3, gap: '13px 16px',
    inputPad: '8px 11px', fontSize: 12.5, labelSize: 10,
    accent: '#374151', accentBg: '#f3f4f6',
    description: 'Record Sheet — compact 3-column layout',
  },
  // Bonafide — fewer fields → 2-column spacious
  BONAFIDE: {
    cols: 2, gap: '20px 28px',
    inputPad: '13px 16px', fontSize: 14, labelSize: 11.5,
    accent: '#B8860B', accentBg: '#fef9e7',
    description: 'Bonafide Certificate — spacious 2-column layout',
  },
  // Study — same as Bonafide
  STUDY: {
    cols: 2, gap: '20px 28px',
    inputPad: '13px 16px', fontSize: 14, labelSize: 11.5,
    accent: '#7c3aed', accentBg: '#f5f3ff',
    description: 'Study Certificate — spacious 2-column layout',
  },
  // ID Card — single wide column (has photo field)
  IDCARD: {
    cols: 2, gap: '16px 22px',
    inputPad: '11px 14px', fontSize: 13, labelSize: 11,
    accent: '#c2185b', accentBg: '#fce4ec',
    description: 'ID Card — standard 2-column layout',
  },
};
const DEFAULT_FORM_CFG = {
  cols: 2, gap: '18px 22px',
  inputPad: '11px 14px', fontSize: 13, labelSize: 11,
  accent: '#6366f1', accentBg: '#eef2ff',
  description: '',
};

// Build the fieldInput style from a form config
const makeFieldInput = (cfg) => ({
  width: '100%', border: '1.5px solid #d1d5db', borderRadius: 8,
  padding: cfg.inputPad, fontSize: cfg.fontSize, color: '#1f2937',
  background: '#fff', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
});

const makeFieldLabel = (cfg) => ({
  display: 'block', fontSize: cfg.labelSize, fontWeight: 700,
  color: '#6b7280', textTransform: 'uppercase',
  letterSpacing: '0.5px', marginBottom: 5,
});

// Build gridTemplateColumns string from col count
const colsToGrid = (n) =>
  Array(n).fill('1fr').join(' ');  // e.g. 3 → '1fr 1fr 1fr'

const DetailsPhase = ({
  template, student, className, sectionName, certTypeKey, certTypeId, branchId, academicYearId,
  certificateTypes,
  fetchStudentDetails,
  fetchStudentAllocation,
  onGenerateCertificate,
}) => {
  const printAreaRef = useRef(null);

  // ── Form layout derived from certificate type ───────────────────────────
  const formCfg    = CERT_FORM_CONFIG[(certTypeKey || '').toUpperCase()] || DEFAULT_FORM_CFG;
  const fInput     = makeFieldInput(formCfg);
  const fLabel     = makeFieldLabel(formCfg);

  const todayISO = new Date().toISOString().split('T')[0];
  const [fieldValues,      setFieldValues]      = useState({ date: todayISO });
  const [tcStyle,          setTcStyle]          = useState('borders');
  const [recordBorder,     setRecordBorder]     = useState(true);
  const [signatureEnabled, setSignatureEnabled] = useState(false);
  const [signatureImages,  setSignatureImages]  = useState({});
  const [selectedSigIdx,   setSelectedSigIdx]   = useState(0);
  const [generating,       setGenerating]       = useState(false);
  const [generateError,    setGenerateError]    = useState('');
  const [generated,        setGenerated]        = useState(null); // { row data }
  const [showPreview,      setShowPreview]      = useState(false);
  const [toast,            setToast]            = useState('');

  // Parse template config
  const cfg = useMemo(() => {
    if (!template) return null;
    try { return JSON.parse(template.template_json); } catch { return null; }
  }, [template]);

  // Dynamic fields from template.
  // PRIMARY: cfg.fields (builder's definitive ordered list of visible fields).
  // MERGED:  any {{variable}} tokens in body HTML not tracked in cfg.fields are appended
  //          (e.g. {{date}} / {{school_code}} in the static RECORD_BODY header).
  // FALLBACK: extract tokens from body HTML (old templates without cfg.fields).
  const dynamicFields = useMemo(() => {
    if (!cfg) return [];
    if (cfg.certType === 'idcard') {
      let keys = cfg.fields?.length > 0
        ? cfg.fields.filter(f => f.visible).map(f => f.key)
        : ['student_name', 'father_name', 'class_name', 'section_name', 'dob', 'student_address', 'phone', 'blood_group'];
      // student_photo is always required for ID cards — it's the circular photo slot
      if (!keys.includes('student_photo')) keys = [...keys, 'student_photo'];
      return keys;
    }
    if (cfg.fields?.length > 0) {
      const hiddenSet  = new Set(cfg.fields.filter(f => !f.visible).map(f => f.key));
      const visibleKeys = cfg.fields.filter(f => f.visible).map(f => f.key);
      const visibleSet  = new Set(visibleKeys);
      // Append any body-HTML tokens not already in cfg.fields and not hidden
      const bodyKeys = extractVariablesFromHtml(cfg.bodyContent || '');
      const extra = bodyKeys.filter(k => !visibleSet.has(k) && !hiddenSet.has(k));
      return [...visibleKeys, ...extra];
    }
    // Fallback for old templates saved before cfg.fields was introduced
    return extractVariablesFromHtml(cfg.bodyContent || '');
  }, [cfg]);

  const hiddenKeys = useMemo(() => {
    if (!cfg?.fields) return [];
    return cfg.fields.filter(f => !f.visible).map(f => f.key);
  }, [cfg]);

  // Sync recordBorder state whenever template changes
  useEffect(() => {
    if (cfg?.certType === 'recordsheet') {
      setRecordBorder(cfg.recordTableBorder !== false);
    }
  }, [cfg]);

  // ── Auto-fill student data ────────────────────────────────────────────────
  useEffect(() => {
    if (!student) return;

    // Current academic year default (e.g. 2026-2027)
    const now = new Date();
    const yr  = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const defaultAcademicYear = `${yr}-${yr + 1}`;

    // Step 1 — immediate fill from student list data
    const base = {};
    const sb = (k, v) => { if (v) base[k] = v; };
    sb('student_name',  student.student_name);
    sb('roll_no',       student.roll_no);
    sb('admission_no',  student.admission_no);
    sb('dob',           student.dob);
    sb('class_name',    className || student.class_name);
    sb('section_name',  sectionName || student.section_name);
    sb('father_name',   student.father_name || student.parent_name);
    sb('mother_name',   student.mother_name);
    sb('phone',         student.student_mobile || student.parent_mobile);
    sb('pen_no',        student.pen_no);
    sb('aadhar_no',     student.adhar_no);   // template token is {{aadhar_no}}, backend column is adhar_no
    sb('admitted_date', student.admitted_date);
    // Profile photo (ID card slot) — prefer base64, then URL; allocation fetch refines later
    sb('student_photo', normalizePhotoSrc(student.profile_image_base || student.profile_image));
    base.academic_year = defaultAcademicYear;

    setFieldValues(prev => ({ ...prev, ...base, date: prev.date || todayISO }));

    // Step 2 — fetch full details via getCertificateAllocations.
    // Branch & academic year are optional — when not chosen they are passed empty
    // and the student is matched by id. Only the certificate type is required.
    const effectiveBranchId   = branchId || '';   // empty when no branch filter chosen
    const effectiveCertTypeId = certTypeId || template?.certificate_type_id;

    if (fetchStudentAllocation && effectiveCertTypeId) {
      fetchStudentAllocation(
        student.student_id,
        effectiveBranchId,
        effectiveCertTypeId,
        academicYearId
      )
        .then(async alloc => {
          console.log('[DetailsPhase] alloc result:', alloc);
          if (!alloc) return;
          const a = {};
          const sa = (k, v) => { if (v != null && v !== '') a[k] = v; };
          sa('student_name',  alloc.student_name);
          sa('admission_no',  alloc.admission_no);   // real admission number
          sa('roll_no',       alloc.roll_no);
          sa('dob',           alloc.dob);
          sa('gender',        alloc.gender);
          sa('class_name',    alloc.class_name);
          sa('section_name',  alloc.section_name);
          sa('father_name',   alloc.father_name);
          sa('mother_name',   alloc.mother_name);
          sa('phone',         alloc.student_mobile || alloc.father_phone);
          sa('pen_no',        alloc.pen_no);
          sa('aadhar_no',     alloc.adhar_no);
          // Use allocation year if present, else keep default
          if (alloc.academic_year) a.academic_year = alloc.academic_year;

          // Profile photo: prefer base64 (profile_image_base), then URL
          const photoSrc = normalizePhotoSrc(alloc.profile_image_base || alloc.profile_image);
          if (photoSrc) {
            if (photoSrc.startsWith('data:')) {
              a.student_photo = photoSrc;
            } else {
              try {
                const res  = await fetch(photoSrc, { mode: 'cors' });
                const blob = await res.blob();
                a.student_photo = await new Promise(resolve => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result);
                  reader.readAsDataURL(blob);
                });
              } catch {
                a.student_photo = photoSrc;
              }
            }
          }

          setFieldValues(prev => ({ ...prev, ...a }));
        });
    }

  }, [student?.student_id]);

  // Live preview HTML
  const previewHtml = useMemo(() => {
    if (!cfg) return '';
    const effectiveSig = signatureEnabled ? signatureImages : {};
    let html = renderCertificateForStudent(cfg, fieldValues, hiddenKeys, effectiveSig);
    if (cfg.certType === 'tc' && tcStyle === 'text') {
      html = '<style>table td,table th,table{border:none!important;border-collapse:collapse!important;}table{border-top:1px solid #999!important;}</style>' + html;
    }
    // Record sheet — toggle table borders via CSS on the saved body so no rows
    // are lost (rebuilding from cfg.fields used to drop any field not in the list).
    if (cfg.certType === 'recordsheet') {
      const savedBorder = cfg.recordTableBorder !== false;
      if (recordBorder !== savedBorder) {
        html = (recordBorder
          ? '<style>.dcb-cert-root table{border-collapse:collapse!important;}.dcb-cert-root table td,.dcb-cert-root table th{border:1px solid #999!important;}</style>'
          : '<style>.dcb-cert-root table,.dcb-cert-root table td,.dcb-cert-root table th{border:none!important;border-collapse:collapse!important;}'
            + '.dcb-cert-root table tr td:last-child::before{content:": ";}</style>'
        ) + html;
      }
    }
    return html;
  }, [cfg, fieldValues, hiddenKeys, tcStyle, recordBorder, signatureEnabled, signatureImages]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const handleFieldChange = (key, val) => setFieldValues(prev => ({ ...prev, [key]: val }));

  const handleGenerate = async () => {
    setGenerateError('');
    setGenerating(true);
    try {
      let certificateBase64 = '';
      if (printAreaRef.current) {
        const restoreImages = await inlineImagesForCapture(printAreaRef.current);
        try {
          const canvas = await html2canvas(printAreaRef.current, { scale: 2, useCORS: true, allowTaint: true });
          certificateBase64 = canvas.toDataURL('image/png');
        } catch {} finally {
          restoreImages();
        }
      }

      const certNo    = `CERT-${student.student_id}-${Date.now()}`;
      const issueDate = fieldValues.date || todayISO;
      const certTypeId = template?.certificate_type_id
        || certificateTypes.find(ct => toFilterKey(ct.certificate_type) === certTypeKey)?.id
        || 1;

      if (onGenerateCertificate) {
        await onGenerateCertificate({
          edu_student_id:          student.student_id,
          certificate_type_id:     certTypeId,
          certificate_no:          certNo,
          issued_date:             issueDate,
          certificate_file_base64: certificateBase64,
        });
      }

      const row = {
        certNo,
        studentName:  student.student_name,
        admissionNo:  fieldValues.admission_no || student.admission_no || '—',
        className:    fieldValues.class_name    || className,
        sectionName:  fieldValues.section_name  || sectionName,
        certType:     TYPE_LABELS[certTypeKey?.toLowerCase()] || certTypeKey,
        templateName: template?.template_name || '—',
        issuedDate:   issueDate,
        html:         previewHtml,
      };
      console.log("row",row)
      setGenerated(row);
      showToast('Certificate generated and saved successfully!');
    } catch (err) {
      const msg = (err.graphQLErrors?.[0]?.message || err.message || 'Failed to save certificate.').replace(/^GraphQL error:\s*/i, '');
      setGenerateError(msg);
    } finally {
      setGenerating(false);
    }
  };

  // Shared ID-card renderer — rasterises each side (front, then back) to a PNG
  // data URL. Print and Download PDF both use this so their output is identical.
  // We capture the ALREADY-RENDERED on-screen cards (#dcb-idcard-front /
  // #dcb-idcard-back) so their background-image (the green wave) and signature
  // are fully loaded — rebuilding off-screen captured them before the webpack
  // image had loaded, leaving a white body + a stray wave-band line in the PDF.
  // The onclone strips rounded corners + shadow so each card prints full-bleed.
  const renderIdCardImages = async () => {
    const root  = printAreaRef.current;
    const front = root.querySelector('#dcb-idcard-front');
    const back  = root.querySelector('#dcb-idcard-back');
    // Capture the actual card element (the 54×86mm div), not its wrapper, so the
    // raster has no stray margin on the right/bottom edges.
    const targets = [front, back].filter(Boolean).map(el => el.firstElementChild || el);
    const list = targets.length ? targets : [root];

    const fixupClone = (cdoc) => {
      // Force full-bleed: square the card corners + drop shadows. Injected as an
      // !important rule so it always wins over the inline border-radius:10px.
      const st = cdoc.createElement('style');
      st.textContent = '#dcb-idcard-front>div,#dcb-idcard-back>div{border-radius:0!important;box-shadow:none!important}';
      (cdoc.head || cdoc.documentElement).appendChild(st);

      ['#dcb-idcard-front', '#dcb-idcard-back'].forEach(sel => {
        const liveWrap  = root.querySelector(sel);
        const cloneWrap = cdoc.querySelector(sel);
        if (!cloneWrap) return;
        cloneWrap.querySelectorAll('*').forEach(n => { if (n.style) n.style.boxShadow = 'none'; });
        const card = cloneWrap.firstElementChild;
        if (card && card.style) { card.style.borderRadius = '0'; card.style.boxShadow = 'none'; }

        // Front only: html2canvas doesn't stretch the flex:1 green body, leaving a
        // white gap at the card bottom. Pin the body to its real on-screen height
        // (the browser lays it out filled) so the captured card fills to the edge.
        if (sel === '#dcb-idcard-front' && liveWrap && card) {
          const liveCard = liveWrap.firstElementChild;
          const liveBody = liveCard && liveCard.children[1];
          const cloneBody = card.children[1];
          if (liveBody && cloneBody) {
            cloneBody.style.flex = 'none';
            cloneBody.style.height = liveBody.offsetHeight + 'px';
          }
        }
      });
    };

    const images = [];
    for (const el of list) {
      const canvas = await html2canvas(el, {
        scale: 4, useCORS: true, allowTaint: true, backgroundColor: '#ffffff',
        onclone: fixupClone,
      });
      images.push(canvas.toDataURL('image/png'));
    }
    return images;
  };

  const handlePrint = async () => {
    if (!printAreaRef.current) return;

    // ID Card: print each side on its OWN portrait CR80 card page (54mm × 86mm).
    // Print the NATIVE card HTML (not a html2canvas raster) so the browser's own
    // engine renders it — flex fills correctly, the wave background loads, and
    // there are no rasterisation seams/white gaps. @page locks the size to the
    // card so it can't land on A4. The iframe is same-origin, so the card's
    // /static/media image URLs resolve.
    if (cfg?.certType === 'idcard') {
      const root  = printAreaRef.current;
      const front = root.querySelector('#dcb-idcard-front');
      const back  = root.querySelector('#dcb-idcard-back');
      const cards = [front, back]
        .filter(Boolean)
        .map(el => el.innerHTML
          .replace(/border-radius\s*:[^;"']+;?/gi, '')
          .replace(/box-shadow\s*:[^;"']+;?/gi, ''));
      const body = (cards.length ? cards : [root.innerHTML])
        .map(c => `<div class="idpage">${c}</div>`)
        .join('');

      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      document.body.appendChild(iframe);
      const cleanup = () => { setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) {} }, 1000); };
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(
        `<!DOCTYPE html><html><head><title>ID Card</title>
        <base href="${window.location.origin}/">
        <style>
          @page{size:54mm 86mm;margin:0}
          *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;box-sizing:border-box}
          html,body{margin:0;padding:0}
          .idpage{width:54mm;height:86mm;overflow:hidden;page-break-after:always}
          .idpage:last-child{page-break-after:auto}
          /* Force each card to fill its page, with a 0.6mm bleed past every edge so
             no white shows after squaring the corners. */
          .idpage>div{width:55.2mm!important;height:87.2mm!important;margin:-0.6mm 0 0 -0.6mm!important;border-radius:0!important}
        </style></head><body>${body}</body></html>`
      );
      doc.close();
      setTimeout(() => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {}
        cleanup();
      }, 500);
      return;
    }

    // Record Sheet: fill the whole A4 page with an EQUAL margin on all four sides.
    // The sheet is rasterised at a fixed width, so its aspect ratio won't match A4
    // — `contain` would leave uneven top/bottom vs left/right gaps. Instead the
    // image fills the margin box exactly (width:100% + height:100%), so its edges
    // sit the SAME distance (the uniform padding) from every page edge. The sheet
    // is mostly a full-width table, so the small vertical scale-to-fit is not
    // visually noticeable while the four margins come out identical.
    if (cfg?.certType === 'recordsheet') {
      const restore = await inlineImagesForCapture(printAreaRef.current);
      let img;
      try {
        const canvas = await html2canvas(printAreaRef.current, { scale: 3, useCORS: true, allowTaint: true, backgroundColor: '#ffffff' });
        img = canvas.toDataURL('image/png');
      } finally {
        restore();
      }

      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      document.body.appendChild(iframe);
      const cleanup = () => { setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) {} }, 1000); };

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(
        `<!DOCTYPE html><html><head><title>Record Sheet</title><style>
          /* Let the print engine own the margin (8mm on every side) so it's a REAL
             reserved margin inside the printable area — not padding that the
             printer's non-printable bottom edge can clip. This keeps all four
             margins equal AND stops the bottom border from being cut off. */
          @page{size:A4 portrait;margin:8mm}
          *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;box-sizing:border-box}
          html,body{margin:0;padding:0;height:100%}
          /* The image fills the page CONTENT box (page minus the 8mm @page margin),
             so the sheet's outer border sits 8mm from every edge. */
          .page{height:100%;}
          .page img{width:100%;height:100%;object-fit:fill;display:block;}
        </style></head><body><div class="page"><img src="${img}" /></div></body></html>`
      );
      doc.close();
      setTimeout(() => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {}
        cleanup();
      }, 500);
      return;
    }

    // Render the certificate to a single image (same as Download PDF/Image) and
    // print THAT, fit to the page. The native-HTML + CSS-transform approach kept
    // letting the browser paginate by the un-transformed height, which clipped
    // the bottom border / spilled onto a 2nd page. A fitted image is reliable:
    // it preserves aspect ratio and the whole bordered frame always fits one page
    // — on A4 OR Letter — with equal margins all around.
    // Add a white strip below the frame so html2canvas can't clip the bottom
    // border at the image edge (it otherwise drops the last border pixels).
    const pEl = printAreaRef.current;
    const prevPB = pEl.style.paddingBottom;
    pEl.style.paddingBottom = '18px';
    const restoreImages = await inlineImagesForCapture(pEl);
    let imgData, imgWmm, imgHmm;
    try {
      // Force html2canvas to render the FULL element height (incl. the bottom
      // border) — it otherwise clips the last pixels, dropping the bottom border.
      const fullH = pEl.scrollHeight;
      const canvas = await html2canvas(pEl, { scale: 3, useCORS: true, allowTaint: true, backgroundColor: '#ffffff', height: fullH, windowHeight: fullH, scrollX: 0, scrollY: 0 });
      imgData = canvas.toDataURL('image/png');
      // Bonafide & Study are short certificates that occupy the TOP HALF of the A4
      // page (so a second certificate — e.g. the Study certificate — can later sit
      // on the bottom half). Other types use the full A4 height. The image is sized
      // in mm from its aspect ratio, fitting the chosen box, then top-aligned.
      const ratio = canvas.width / canvas.height;
      const isHalfPage = cfg?.certType === 'bonafide' || cfg?.certType === 'study';
      if (isHalfPage) {
        // Bonafide/Study: fill most of the width (small left/right margins) and
        // top-align, leaving a little space below. Height follows the aspect ratio.
        const sideM = 10, topM = 10, bottomGap = 12;
        imgWmm = 210 - sideM * 2;          // 190mm wide — small L/R margins
        imgHmm = imgWmm / ratio;
        const maxH = 297 - topM - bottomGap;
        if (imgHmm > maxH) { imgHmm = maxH; imgWmm = imgHmm * ratio; }
      } else {
        const availW = 198, availH = 285;  // full A4 − margins
        imgWmm = availW;
        imgHmm = availW / ratio;
        if (imgHmm > availH) { imgHmm = availH; imgWmm = availH * ratio; }
      }
    } finally {
      restoreImages();
      pEl.style.paddingBottom = prevPB;
    }

    // Print via a hidden iframe so no new browser tab/window is opened.
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);

    const cleanup = () => { setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) {} }, 1000); };

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(
      `<!DOCTYPE html><html><head><title>Certificate</title><style>
        /* A4 page. The certificate is top-aligned (and centred horizontally) so a
           short cert sits in the TOP HALF, leaving the bottom half for a second
           certificate (e.g. the Study certificate on the same A4 sheet). */
        @page{size:A4 portrait;margin:0}
        *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;box-sizing:border-box}
        html,body{margin:0;padding:0}
        .page{width:210mm;height:297mm;display:flex;justify-content:center;align-items:flex-start;padding-top:10mm;}
        .page img{display:block;width:${imgWmm}mm;height:${imgHmm}mm;}
      </style></head><body><div class="page"><img src="${imgData}" /></div></body></html>`
    );
    doc.close();

    // Wait for the image to load before printing.
    setTimeout(() => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {}
      cleanup();
    }, 500);
  };

  const handlePDF = async () => {
    if (!printAreaRef.current) return;

    const admissionNo = fieldValues.admission_no || student?.admission_no || '';
    const studentName = student?.student_name || fieldValues.student_name || '';
    const fileName = [admissionNo, studentName].filter(Boolean).join('_')
      .replace(/[^a-zA-Z0-9_-]+/g, '_') || (template?.template_name || 'Certificate').replace(/\s+/g, '_');

    try {
      // ID Card: one CR80 portrait page (54mm × 86mm) per side, front then back.
      // Uses the same rasterised card images as Print, so PDF === Print output.
      if (cfg?.certType === 'idcard') {
        const images = await renderIdCardImages();
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [54, 86] });
        const B = 0.6; // mm bleed — overscan the page so no white shows at the edges
        images.forEach((img, i) => {
          if (i > 0) pdf.addPage([54, 86], 'portrait');
          pdf.addImage(img, 'PNG', -B, -B, 54 + B * 2, 86 + B * 2);
        });
        pdf.save(`${fileName}.pdf`);
        return;
      }

      // Inline the logo/watermark as base64 so they render in the PDF (html2canvas
      // otherwise leaves cross-origin images blank). Also add a white strip below
      // so the bottom border isn't clipped at the image edge.
      const pEl = printAreaRef.current;
      const prevPB = pEl.style.paddingBottom;
      pEl.style.paddingBottom = '18px';
      const restoreImages = await inlineImagesForCapture(pEl);
      let canvas;
      try {
        // Force the FULL element height so the bottom border isn't clipped.
        const fullH = pEl.scrollHeight;
        canvas = await html2canvas(pEl, { scale: 3, useCORS: true, allowTaint: true, backgroundColor: '#ffffff', height: fullH, windowHeight: fullH, scrollX: 0, scrollY: 0 });
      } finally {
        restoreImages();
        pEl.style.paddingBottom = prevPB;
      }
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      // Record Sheet: fill the full A4 page with an EQUAL 8mm margin on all four
      // sides (stretch to fill), matching the Print output. The sheet isn't exactly
      // A4-shaped, so keeping its aspect ratio would leave the top/bottom margins
      // unequal to the left/right ones — filling the margin box makes all four equal.
      if (cfg?.certType === 'recordsheet') {
        const M = 8;
        pdf.addImage(imgData, 'PNG', M, M, pageW - M * 2, pageH - M * 2);
        pdf.save(`${fileName}.pdf`);
        return;
      }
      // Fit the whole certificate onto ONE A4 page within a uniform margin, then
      // top-align it (block sits at the top, equal space on left & right) instead
      // of centering vertically — which otherwise leaves a gap above the border.
      const M = 6; // mm margin on all sides
      const availW = pageW - M * 2;
      const availH = pageH - M * 2;
      const ratio = canvas.width / canvas.height;
      const isHalfPage = cfg?.certType === 'bonafide' || cfg?.certType === 'study';
      let renderW, renderH, x, y;
      if (isHalfPage) {
        const sideM = 10, topM = 10, bottomGap = 12;
        renderW = pageW - sideM * 2;       // 190mm — small L/R margins
        renderH = renderW / ratio;
        const maxH = pageH - topM - bottomGap;
        if (renderH > maxH) { renderH = maxH; renderW = renderH * ratio; }
        x = (pageW - renderW) / 2;         // centred horizontally
        y = topM;                          // top-aligned
      } else {
        const M = 6;
        const availW = pageW - M * 2;
        const availH = pageH - M * 2;
        renderW = availW;
        renderH = availW / ratio;
        if (renderH > availH) { renderH = availH; renderW = availH * ratio; }
        x = (pageW - renderW) / 2;
        y = (pageH - renderH) / 2;
      }
      pdf.addImage(imgData, 'PNG', x, y, renderW, renderH);
      pdf.save(`${fileName}.pdf`);
    } catch (err) { console.error('PDF error:', err); }
  };

  // Download the certificate as a PNG image (same raster used by Print / PDF).
  // ID cards download each side (front, then back) as its own .png.
  const handleImage = async () => {
    if (!printAreaRef.current) return;

    const admissionNo = fieldValues.admission_no || student?.admission_no || '';
    const studentName = student?.student_name || fieldValues.student_name || '';
    const fileName = [admissionNo, studentName].filter(Boolean).join('_')
      .replace(/[^a-zA-Z0-9_-]+/g, '_') || (template?.template_name || 'Certificate').replace(/\s+/g, '_');

    const saveImg = (dataUrl, name) => {
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    try {
      if (cfg?.certType === 'idcard') {
        const images = await renderIdCardImages();
        const sides = ['front', 'back'];
        images.forEach((img, i) => {
          const suffix = images.length > 1 ? `_${sides[i] || i + 1}` : '';
          saveImg(img, `${fileName}${suffix}.png`);
        });
        return;
      }

      const restoreImages = await inlineImagesForCapture(printAreaRef.current);
      let canvas;
      try {
        canvas = await html2canvas(printAreaRef.current, { scale: 3, useCORS: true, allowTaint: true, backgroundColor: '#ffffff' });
      } finally {
        restoreImages();
      }
      saveImg(canvas.toDataURL('image/png'), `${fileName}.png`);
    } catch (err) { console.error('Image download error:', err); }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ width: '100%' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9999,
          background: '#10b981', color: '#fff', borderRadius: 12,
          padding: '12px 20px', fontWeight: 600, fontSize: 13,
          boxShadow: '0 8px 32px #10b98140', display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span>✓</span> {toast}
        </div>
      )}

      {/* TC Style toggle */}
      {cfg?.certType === 'tc' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>Table Style:</span>
          {['borders', 'text'].map(s => (
            <button key={s} onClick={() => setTcStyle(s)} style={{
              border: tcStyle === s ? '2px solid #6366f1' : '2px solid #e2e8f0',
              background: tcStyle === s ? '#6366f1' : '#fff', color: tcStyle === s ? '#fff' : '#374151',
              borderRadius: 20, padding: '6px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 13, transition: 'all 0.15s',
            }}>
              {s === 'borders' ? 'With Borders' : 'Text Only'}
            </button>
          ))}
        </div>
      )}


      {/* Signature section — available for all certificate types including ID card */}
      {cfg && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox" checked={signatureEnabled}
                onChange={e => { setSignatureEnabled(e.target.checked); if (!e.target.checked) setSignatureImages({}); }}
                style={{ width: 16, height: 16, accentColor: '#6366f1', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>Add Signature</span>
            </label>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>Upload signature image to display on the certificate</span>
          </div>

          {signatureEnabled && (() => {
            const sigs = cfg.signatures || [];
            const isIdCard = cfg.certType === 'idcard';
            const sigIdx = sigs.length >= 2 ? selectedSigIdx : 0;
            const currentImg = signatureImages[sigIdx];
            return (
              <div style={{ marginTop: 14 }}>
                {/* ID card has no cfg.signatures — label it as Principal */}
                {isIdCard && (
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                    Principal Signature
                  </div>
                )}
                {!isIdCard && sigs.length >= 2 && (
                  <div style={{ marginBottom: 12 }}>
                    <label style={ciStyle.label}>Select Signatory</label>
                    <select value={selectedSigIdx} onChange={e => setSelectedSigIdx(Number(e.target.value))} style={{ ...ciStyle.input, maxWidth: 320 }}>
                      {sigs.map((sig, idx) => <option key={idx} value={idx}>{sig.label}</option>)}
                    </select>
                  </div>
                )}
                <label style={{ display: 'block', border: '2px dashed #d1d5db', borderRadius: 10, padding: '14px 20px', cursor: 'pointer', textAlign: 'center', background: currentImg ? '#f0fdf4' : '#fafafa', maxWidth: 360, borderColor: currentImg ? '#86efac' : '#d1d5db' }}>
                  {currentImg ? (
                    <div>
                      <img src={currentImg} alt="sig" style={{ maxWidth: 200, maxHeight: 70, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
                      <button type="button" onClick={e => { e.preventDefault(); setSignatureImages(prev => { const n = { ...prev }; delete n[sigIdx]; return n; }); }} style={{ marginTop: 8, background: 'none', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>× Remove</button>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: 26, marginBottom: 4 }}>✍</div>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>Click to upload signature image</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>PNG, JPG, SVG — max 2 MB · transparent background recommended</div>
                    </>
                  )}
                  <input type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={e => {
                      const file = e.target.files[0]; if (!file) return;
                      const reader = new FileReader();
                      reader.onloadend = () => setSignatureImages(prev => ({ ...prev, [sigIdx]: reader.result }));
                      reader.readAsDataURL(file);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            );
          })()}
        </div>
      )}

      {/* Photo position / zoom — ID card only, once a photo is present */}
      {cfg?.certType === 'idcard' && fieldValues.student_photo && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <i className="fal fa-crop-alt" style={{ color: formCfg.accent }} />
              <span style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>Adjust Photo</span>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>Move and zoom the photo inside its frame</span>
            </div>
            <button
              type="button"
              onClick={() => setFieldValues(prev => ({ ...prev, photo_pos_x: 50, photo_pos_y: 50, photo_zoom: 1 }))}
              style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, color: '#6b7280', padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}
            >
              Reset
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
            {[
              { key: 'photo_pos_x', label: 'Horizontal', min: 0, max: 100, step: 1, def: 50, suffix: '%' },
              { key: 'photo_pos_y', label: 'Vertical',   min: 0, max: 100, step: 1, def: 50, suffix: '%' },
              { key: 'photo_zoom',  label: 'Zoom',        min: 1, max: 3,   step: 0.05, def: 1, suffix: '×' },
            ].map(({ key, label, min, max, step, def, suffix }) => {
              const val = fieldValues[key] != null ? Number(fieldValues[key]) : def;
              return (
                <div key={key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</label>
                    <span style={{ fontSize: 12, fontWeight: 700, color: formCfg.accent }}>{val}{suffix}</span>
                  </div>
                  <input
                    type="range"
                    min={min} max={max} step={step} value={val}
                    onChange={e => handleFieldChange(key, Number(e.target.value))}
                    style={{ width: '100%', accentColor: formCfg.accent, cursor: 'pointer' }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Main layout: form (left) + live preview (right) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 20, alignItems: 'start' }}>

        {/* Left: Field form */}
        <div style={card}>

          {/* Form header — colour-coded by cert type */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
            padding: '10px 14px', borderRadius: 10,
            background: formCfg.accentBg,
            border: `1.5px solid ${formCfg.accent}22`,
          }}>
            <div style={{
              background: formCfg.accent, color: '#fff',
              borderRadius: 8, width: 32, height: 32, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
            }}>
              <i className={CERT_TYPE_ICONS[(certTypeKey || '').toUpperCase()]?.icon || 'fal fa-file-alt'} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: formCfg.accent }}>
                Fill Certificate Details
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
                {TYPE_LABELS[(certTypeKey || '').toLowerCase()] || certTypeKey} ·&nbsp;
                <span style={{ color: '#6366f1' }}>
                  {formCfg.cols}-column layout · {dynamicFields.length} field{dynamicFields.length !== 1 ? 's' : ''}
                </span>
                &nbsp;· Preview updates in real-time
              </div>
            </div>
          </div>

          {/* Record Sheet — border style toggle (shown inside the form card) */}
          {cfg?.certType === 'recordsheet' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 20, marginBottom: 14,
              padding: '8px 14px', background: '#f9fafb',
              borderRadius: 8, border: '1px solid #e5e7eb',
            }}>
              <span style={{ fontWeight: 700, fontSize: 12, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Table Style:
              </span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                <input type="radio" name="ccRecordBorder" checked={recordBorder} onChange={() => setRecordBorder(true)} />
                With Table Border
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                <input type="radio" name="ccRecordBorder" checked={!recordBorder} onChange={() => setRecordBorder(false)} />
                Without Table Border
              </label>
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: colsToGrid(formCfg.cols),
            gap: formCfg.gap,
          }}>
            {dynamicFields.map(key => {
              const cfgField = cfg?.fields?.find(f => f.key === key);
              const label   = (cfgField?.label) || fieldLabel(key);
              const type    = FIELD_TYPES[key] || 'text';
              const options = FIELD_OPTIONS[key];
              const rawPhoto = isRawPhotoField(key);
              const imgField = isImageField(key);

              // ── Photo upload (full-width) ──────────────────────────────
              if (rawPhoto) return (
                <div key={key} style={{ gridColumn: '1 / -1' }}>
                  <label style={fLabel}>{label}</label>
                  <label style={{
                    display: 'block', border: `2px dashed ${fieldValues[key] ? '#86efac' : '#d1d5db'}`,
                    borderRadius: 10, padding: '14px 20px', cursor: 'pointer', textAlign: 'center',
                    background: fieldValues[key] ? '#f0fdf4' : '#fafafa', maxWidth: 380,
                  }}>
                    {fieldValues[key] ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <img src={fieldValues[key]} alt={label} style={{ borderRadius: '50%', width: 72, height: 72, objectFit: 'cover', border: '2px solid #86efac' }} />
                        <button type="button" onClick={e => { e.preventDefault(); handleFieldChange(key, ''); }}
                          style={{ background: 'none', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}>
                          × Remove
                        </button>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: 26, marginBottom: 4 }}>📷</div>
                        <div style={{ fontSize: 13, color: '#6b7280' }}>Click to upload Student Photo</div>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>PNG, JPG — max 2 MB · circular</div>
                      </>
                    )}
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={e => {
                        const file = e.target.files[0]; if (!file) return;
                        const reader = new FileReader();
                        reader.onloadend = () => handleFieldChange(key, reader.result);
                        reader.readAsDataURL(file);
                      }}
                    />
                  </label>
                </div>
              );

              // ── Image / signature upload (full-width) ─────────────────
              if (imgField) return (
                <div key={key} style={{ gridColumn: '1 / -1' }}>
                  <label style={fLabel}>{label}</label>
                  <label style={{
                    display: 'block', border: '2px dashed #d1d5db', borderRadius: 10,
                    padding: '14px 20px', cursor: 'pointer', textAlign: 'center',
                    background: fieldValues[key] ? '#f0fdf4' : '#fafafa', maxWidth: 380,
                  }}>
                    {fieldValues[key] ? (
                      <div>
                        <img src={fieldValues[key]} alt={label} style={{ maxWidth: 200, maxHeight: 80, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
                        <button type="button" onClick={e => { e.preventDefault(); handleFieldChange(key, ''); }}
                          style={{ marginTop: 8, background: 'none', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>
                          × Remove
                        </button>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: 26, marginBottom: 4 }}>📷</div>
                        <div style={{ fontSize: 13, color: '#6b7280' }}>Click to upload {label}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>PNG, JPG, SVG — max 2 MB</div>
                      </>
                    )}
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={e => {
                        const file = e.target.files[0]; if (!file) return;
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          const imgHtml = `<img src="${reader.result}" alt="${label}" style="max-width:80px;max-height:80px;object-fit:contain;vertical-align:middle;" />`;
                          handleFieldChange(key, imgHtml);
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                  </label>
                </div>
              );

              // ── Phone field with +91 prefix and 10-digit validation ───
              if (key === 'phone') return (
                <div key={key}>
                  <label style={fLabel}>{label}</label>
                  <div style={{ display: 'flex', alignItems: 'stretch' }}>
                    <span style={{
                      background: '#f0f2f5',
                      border: `1.5px solid ${fieldValues[key] ? `${formCfg.accent}55` : '#d1d5db'}`,
                      borderRight: 'none', borderRadius: '8px 0 0 8px',
                      padding: '0 10px', display: 'flex', alignItems: 'center',
                      fontSize: fInput.fontSize, color: '#6b7280', whiteSpace: 'nowrap', flexShrink: 0,
                    }}>+91</span>
                    <input
                      type="tel"
                      style={{
                        ...fInput, borderRadius: '0 8px 8px 0', borderLeft: 'none', flex: 1, minWidth: 0,
                        borderColor: fieldValues[key] ? `${formCfg.accent}55` : '#d1d5db',
                      }}
                      value={(fieldValues[key] || '').replace(/^\+?91\s*/, '').replace(/\D/g, '').slice(0, 10)}
                      placeholder="10-digit mobile"
                      maxLength={10}
                      onChange={e => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleFieldChange(key, digits ? '+91 ' + digits : '');
                      }}
                    />
                  </div>
                </div>
              );

              // ── Regular text / date / select field ────────────────────
              return (
                <div key={key}>
                  <label style={fLabel}>{label}</label>
                  {options ? (
                    <select
                      style={{ ...fInput, borderColor: '#d1d5db' }}
                      value={fieldValues[key] || ''}
                      onChange={e => handleFieldChange(key, e.target.value)}
                    >
                      <option value="">-- Select --</option>
                      {options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input
                      type={type}
                      style={{ ...fInput, borderColor: fieldValues[key] ? `${formCfg.accent}55` : '#d1d5db' }}
                      value={fieldValues[key] || ''}
                      placeholder={SAMPLE_DATA[key] ? `e.g. ${SAMPLE_DATA[key]}` : label}
                      onChange={e => handleFieldChange(key, e.target.value)}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Error */}
          {generateError && (
            <div style={{ marginTop: 16, background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <i className="fal fa-exclamation-circle" style={{ color: '#dc2626', fontSize: 18, marginTop: 1, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#dc2626', marginBottom: 2 }}>Certificate not generated</div>
                <div style={{ fontSize: 13, color: '#b91c1c' }}>{generateError}</div>
              </div>
              <button onClick={() => setGenerateError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18 }}>×</button>
            </div>
          )}

          {/* Action buttons — Generate button uses cert-type accent colour */}
          <div style={{ marginTop: 22, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <button
              onClick={() => setShowPreview(true)}
              style={{
                background: '#fff',
                color: formCfg.accent,
                border: `2px solid ${formCfg.accent}`,
                borderRadius: 10,
                padding: '11px 24px', fontWeight: 700, fontSize: 14,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
                transition: 'all 0.2s',
              }}
            >
              <i className="fal fa-eye" /> View Certificate
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating}
              style={{
                background: generating
                  ? '#9ca3af'
                  : `linear-gradient(135deg, ${formCfg.accent}, ${formCfg.accent}cc)`,
                color: '#fff', border: 'none', borderRadius: 10,
                padding: '11px 28px', fontWeight: 700, fontSize: 14,
                cursor: generating ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
                boxShadow: generating ? 'none' : `0 4px 14px ${formCfg.accent}44`,
                transition: 'all 0.2s',
              }}
            >
              {generating
                ? <><i className="fal fa-spinner fa-spin" /> Saving…</>
                : <><i className="fal fa-file-check" /> Generate Certificate</>
              }
            </button>
            {generated && (
              <>
                <button onClick={handlePrint} style={actionBtn('#0891b2')}>🖨 Print</button>
                <button onClick={handlePDF}   style={actionBtn('#16a34a')}>📥 Download PDF</button>
                <button onClick={handleImage} style={actionBtn('#9333ea')}>🖼 Download Image</button>
              </>
            )}
          </div>
        </div>

        {/* Right: live preview */}
        <div style={{ ...card, padding: '16px', position: 'sticky', top: 20 }}>

          {/* Label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <span style={{ width: 8, height: 8, background: formCfg.accent, borderRadius: '50%', display: 'inline-block' }} />
            <span style={{ fontWeight: 700, fontSize: 12, color: '#374151', textTransform: 'uppercase', letterSpacing: 1 }}>
              Live Preview
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: formCfg.accent, fontWeight: 600, background: formCfg.accentBg, borderRadius: 5, padding: '2px 8px' }}>
              {TYPE_LABELS[(certTypeKey || '').toLowerCase()] || certTypeKey}
            </span>
          </div>

          {/*
            Equal-gap framed preview.
            - Outer wrapper: equal 14px padding on all 4 sides → creates the visual "frame gap"
            - zoom: 0.43 → scales the 794px A4 certificate down; zoom affects layout too
              so no empty space / scroll issues.
            - flex center → horizontally centers the scaled cert inside the frame.
          */}
          <div style={{
            background: '#e8edf5',
            borderRadius: 10,
            padding: '14px',
            border: '1px solid #d0d8e8',
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden',
          }}>
            <div
              style={{
                zoom: 0.43,                 /* scales layout + visuals equally — equal gaps on all sides */
                width: 794,
                background: '#fff',
                boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
                flexShrink: 0,
              }}
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>

        </div>

      </div>

      {/* ── Generated table ── */}
      {generated && (
        <div style={{ ...card, marginTop: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1f2937', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: 8, width: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>
            Generated Certificate
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>Certificate has been saved to the system. Details below.</div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Cert No', 'Student Name', 'Admission No', 'Class', 'Section', 'Cert Type', 'Template', 'Issued Date', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={td}><span style={{ background: '#ede9fe', color: '#5b21b6', borderRadius: 6, padding: '3px 8px', fontWeight: 600, fontSize: 12 }}>{generated.certNo.slice(-12)}</span></td>
                  <td style={{ ...td, fontWeight: 700, color: '#1f2937' }}>{generated.studentName}</td>
                  <td style={td}>{generated.admission_no}</td>
                  <td style={td}>{generated.className}</td>
                  <td style={td}>{generated.sectionName}</td>
                  <td style={td}><span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 6, padding: '3px 8px', fontSize: 12, fontWeight: 600 }}>{generated.certType}</span></td>
                  <td style={td}>{generated.templateName}</td>
                  <td style={td}>{generated.issuedDate}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => setShowPreview(true)} title="View" style={iconBtn('#6366f1')}><i className="fal fa-eye" /></button>
                      <button onClick={handlePrint}               title="Print" style={iconBtn('#0891b2')}><i className="fal fa-print" /></button>
                      <button onClick={handlePDF}                 title="Download PDF" style={iconBtn('#16a34a')}><i className="fal fa-file-pdf" /></button>
                      <button onClick={handleImage}               title="Download Image" style={iconBtn('#9333ea')}><i className="fal fa-image" /></button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Hidden element for PDF capture */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <div ref={printAreaRef} style={{ width: 794, background: '#fff' }} dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </div>

      {/* Full preview modal */}
      {showPreview && (
        <TemplatePreview
          html={previewHtml}
          title={template?.template_name || 'Certificate Preview'}
          fileName={(template?.template_name || 'certificate').replace(/\s+/g, '_')}
          onClose={() => setShowPreview(false)}
          showExportButtons={false}
        />
      )}
    </div>
  );
};

// ─── Mini style helpers ───────────────────────────────────────────────────────

const card    = { background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 14, padding: '20px 22px' };
const cardHdr = { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 };
const td      = { padding: '12px 14px', color: '#374151', verticalAlign: 'middle' };

const iconWrap = (bg, color) => ({
  background: bg, color, borderRadius: 8, width: 32, height: 32,
  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0,
});

const actionBtn = (color) => ({
  background: `${color}12`, color, border: `1.5px solid ${color}40`, borderRadius: 9,
  padding: '9px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
});

const iconBtn = (color) => ({
  background: `${color}12`, color, border: `1px solid ${color}30`, borderRadius: 7,
  width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', fontSize: 14,
});

const Req   = () => <span style={{ color: '#ef4444' }}>*</span>;
const ErrMsg = ({ children, style }) => (
  <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4, ...style }}>
    <i className="fal fa-exclamation-circle" style={{ marginRight: 4 }} />{children}
  </div>
);

// ─── Loading Screen ──────────────────────────────────────────────────────────

const CERT_TYPE_ICONS = {
  TC:          { icon: 'fal fa-file-alt',        color: '#1a237e', bg: '#e8eaf6' },
  BONAFIDE:    { icon: 'fal fa-award',            color: '#B8860B', bg: '#fef9e7' },
  STUDY:       { icon: 'fal fa-graduation-cap',  color: '#7c3aed', bg: '#f5f3ff' },
  RECORDSHEET: { icon: 'fal fa-table',            color: '#374151', bg: '#f3f4f6' },
  IDCARD:      { icon: 'fal fa-id-card',          color: '#c2185b', bg: '#fce4ec' },
};

// ─── Root component ───────────────────────────────────────────────────────────

const CreateCertifictae1 = ({
  savedTemplates        = [],
  certificateTypes      = [],
  existingCertificates  = [],
  deleteTemplate,
  onGenerateCertificate,
  branches             = [],
  streams              = [],
  allClasses           = [],
  students             = [],
  academicYears        = [],
  fetchStudentDetails  = null,
  fetchStudentAllocation = null,
}) => {
  const [context, setContext] = useState(null);

  const handleSelectionComplete = (ctx) => setContext(ctx);
  const handleReset             = ()    => setContext(null);

  return (
    <div style={{ padding: '4px 0' }}>
      <SelectionPanel
        branches={branches}
        streams={streams}
        allClasses={allClasses}
        students={students}
        certificateTypes={certificateTypes}
        savedTemplates={savedTemplates}
        existingCertificates={existingCertificates}
        academicYears={academicYears}
        onComplete={handleSelectionComplete}
        onReset={handleReset}
      />

      {context && (
        <div style={{ marginTop: 24 }}>
          <DetailsPhase
            key={`${context.student.student_id}_${context.template.id}_${context.certTypeKey}`}
            template={context.template}
            student={context.student}
            className={context.className}
            sectionName={context.sectionName}
            certTypeKey={context.certTypeKey}
            certTypeId={context.certTypeId}
            branchId={context.branchId}
            academicYearId={context.academicYearId}
            certificateTypes={certificateTypes}
            fetchStudentDetails={fetchStudentDetails}
            fetchStudentAllocation={fetchStudentAllocation}
            onGenerateCertificate={onGenerateCertificate}
          />
        </div>
      )}
    </div>
  );
};

export default CreateCertifictae1;
